import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LayoutDashboard, MessageSquare, Clapperboard, ImagePlus, FolderClock, LogOut, CreditCard, Crown, Sprout, Rocket, GraduationCap, Bug, Lock, Star, Plug, Gift, Sparkles, Zap, Trash2, Palette, Bot } from "lucide-react";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useTheme } from "@/context/ThemeContext";
import { useT } from "@/i18n";
import { api } from "@/lib/api";

const PLAN_ICON = { free: Sprout, creator: Star, pro: Crown, power: Zap };
const PLAN_COLOR = {
  free: "text-emerald-700 bg-emerald-50 border-emerald-300",
  creator: "text-zinc-800 bg-gradient-to-r from-zinc-100 via-slate-100 to-zinc-200 border-zinc-400 shadow-sm",
  pro: "text-amber-900 bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-200 border-amber-400 shadow-sm",
  power: "text-purple-900 bg-gradient-to-r from-purple-100 via-fuchsia-100 to-purple-200 border-purple-400 shadow-sm",
};

export default function Sidebar({ inDrawer = false, onNavigate }) {
  const { user, logout } = useAuth();
  const { isHidden, isComingSoon, isNew } = useFeatureFlags();
  const navigate = useNavigate();
  const { t } = useT();
  const { theme, setTheme, themes, customColor, setCustomColor } = useTheme();
  const [openReports, setOpenReports] = useState(0);
  const [pendingChats, setPendingChats] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Just-completed transition: when pending count drops, briefly show a
  // green "✓ N done" badge for 3 s so the user gets a clear visual
  // signal that replies finished. Stores the count that just landed.
  const [justFinished, setJustFinished] = useState(0);
  const prevPendingRef = useRef(0);

  useEffect(() => {
    if (pendingChats < prevPendingRef.current) {
      const finished = prevPendingRef.current - pendingChats;
      setJustFinished(finished);
      const t = setTimeout(() => setJustFinished(0), 3500);
      prevPendingRef.current = pendingChats;
      return () => clearTimeout(t);
    }
    prevPendingRef.current = pendingChats;
  }, [pendingChats]);

  // Poll backend for pending in-flight chat replies. When the user types a
  // message and switches tabs, the AI keeps generating in the background
  // and saves to MongoDB when done. This badge tells them "you have a
  // reply waiting" so they don't forget about it.
  //
  // Also listens for a custom event so the chat page can mark "I just
  // sent a message" → badge appears INSTANTLY without waiting for the
  // next poll cycle. Avoids the 3-8s lag the user complained about.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/chat/pending");
        if (!cancelled) setPendingChats(data?.count || 0);
      } catch { /* ignore */ }
    };
    tick();
    // 3s feels live and matches the speed users expect from a chat UI.
    const id = setInterval(tick, 3_000);
    // Optimistic bump when Chat dispatches `lumioos:chat-pending-bump`
    // so the badge appears <100ms after the user clicks Send.
    const onBump = (e) => {
      const delta = (e?.detail?.delta) || 1;
      setPendingChats((c) => Math.max(0, c + delta));
      // Schedule a confirmatory poll in 1.5s in case bump was wrong.
      setTimeout(tick, 1500);
    };
    window.addEventListener("lumioos:chat-pending-bump", onBump);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("lumioos:chat-pending-bump", onBump);
    };
  }, [user]);

  // Poll the unresolved bug-report count every minute so the admin sees new
  // tickets without refreshing. Lightweight call — admin-only endpoint.
  // Also listens for an in-app event so resolving/deleting from the admin
  // page updates the badge instantly without waiting for the next poll.
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/reports");
        if (!cancelled) setOpenReports((data || []).filter((r) => r.status !== "resolved").length);
      } catch { /* ignore */ }
    };
    tick();
    const t1 = setInterval(tick, 60_000);
    const onUpdated = () => tick();
    window.addEventListener("lumioos:reports-updated", onUpdated);
    return () => {
      cancelled = true;
      clearInterval(t1);
      window.removeEventListener("lumioos:reports-updated", onUpdated);
    };
  }, [user]);

  // Browser title indicator — when one or more AI replies are still
  // generating in the background, prepend "(N) " to the tab title so users
  // see it even from other browser tabs / windows. We snapshot the
  // original title once on mount and restore it when count drops to 0.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const ORIGINAL_TITLE = document.title.replace(/^\(\d+\)\s*/, "");
    if (pendingChats > 0) {
      document.title = `(${pendingChats}) ${ORIGINAL_TITLE}`;
    } else {
      document.title = ORIGINAL_TITLE;
    }
  }, [pendingChats]);

  // Free plan loses access to the heavy AI tools — surface that in the
  // sidebar too (not just the dashboard cards) so the user understands
  // exactly which features unlock with an upgrade. Admin/Creator/Pro pass.
  const planId = user?.plan || "free";
  const isPaid = !!user?.is_admin || planId === "creator" || planId === "pro" || planId === "power";
  // Game Studio is Pro/Power ONLY — Creator does NOT see the entry. Used by
  // the rawItems filter below + the navigate-to-billing CTA on the page.
  const isProOrPower = !!user?.is_admin || planId === "pro" || planId === "power";

  // Each nav item carries an optional feature_key. We filter out items that
  // the admin has marked `hidden`, and visually mute the ones marked
  // `coming_soon`. Plan-lock (Free → Video/Interview) is a separate concern.
  const rawItems = [
    { to: "/app", icon: LayoutDashboard, label: t("side_dashboard"), testid: "nav-dashboard", end: true },
    { to: "/app/chat", icon: MessageSquare, label: t("side_chat"), testid: "nav-chat", feature: "chat", badge: pendingChats, pendingStyle: true },
    { to: "/app/interview", icon: GraduationCap, label: t("side_interview"), testid: "nav-interview", locked: !isPaid, feature: "interview_practice" },
    { to: "/app/projects", icon: FolderClock, label: t("side_projects"), testid: "nav-projects", feature: "projects" },
    { to: "/app/integrations", icon: Plug, label: t("side_integrations") || "Integrations", testid: "nav-integrations", adminOnly: true, feature: "integrations_module" },
    { to: "/app/referrals", icon: Gift, label: t("side_referrals") || "Refer & Earn", testid: "nav-referrals" },
    // NOTE: Salesforce nav intentionally NOT in the sidebar — its entry point lives
    // inside the AI Chat header (visible only when user.sf_pro_active is true).
    { to: "/app/billing", icon: CreditCard, label: t("side_billing"), testid: "nav-billing" },
  ];
  // Master-switch features (like `integrations_module`) hide the item even for
  // admins, so the admin can shut the entire module off site-wide with one click.
  const MASTER_HIDE_FEATURES = ["integrations_module"];
  const items = rawItems
    .filter((it) => !it.adminOnly || user?.is_admin)
    // Items flagged `proOrPowerOnly` ONLY appear for Pro / Power / Admin.
    // Free + Creator never see them in the sidebar — they discover the
    // feature exclusively via the billing page upgrade copy.
    .filter((it) => !it.proOrPowerOnly || isProOrPower)
    .filter((it) => {
      if (!it.feature) return true;
      // Master switches apply to admin too
      if (MASTER_HIDE_FEATURES.includes(it.feature) && isHidden(it.feature)) return false;
      // Per-platform / per-feature hides only affect non-admins
      return user?.is_admin || !isHidden(it.feature);
    })
    .map((it) => ({
      ...it,
      comingSoon: !!it.feature && isComingSoon(it.feature),
      isNew: !!it.feature && isNew(it.feature),
    }));
  if (user?.is_admin) {
    items.push({ to: "/app/admin/ai-workforce", icon: Bot, label: t("side_ai_workforce") || "AI Workforce", testid: "nav-ai-workforce" });
    items.push({ to: "/app/admin", icon: Crown, label: t("side_admin"), testid: "nav-admin", badge: openReports });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDeleteAccount = () => setDeleteDialogOpen(true);

  const handleDeleteSuccess = async () => {
    alert(
      t("delete_account_ok") ||
        "Your account has been deleted. A confirmation email is on the way."
    );
    await logout();
    window.location.href = "/";
  };

  const planKey = user?.plan || "free";
  const PlanIcon = PLAN_ICON[planKey] || Sprout;
  const planLabel = planKey;
  const planColorClass = PLAN_COLOR[planKey] || PLAN_COLOR.free;

  return (
    <aside
      className={
        inDrawer
          ? "flex flex-col w-full bg-white h-full"
          : "hidden md:flex md:flex-col md:w-[260px] border-r border-zinc-200 bg-white sticky top-0 h-screen"
      }
      data-testid={inDrawer ? "sidebar-drawer" : "sidebar"}
    >
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between">
        <Logo size="md" />
        <LangToggle />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {items.map((item) => {
          // Coming soon → muted, not clickable, "soon" badge. Highest precedence
          // so even paid users see it consistently.
          if (item.comingSoon) {
            return (
              <div
                key={item.to}
                data-testid={`${item.testid}-coming-soon`}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md text-zinc-400 cursor-not-allowed select-none"
                title="Coming soon"
              >
                <item.icon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">
                  Soon
                </span>
              </div>
            );
          }
          // Plan-locked (Free user → Video & Interview): greyed-out button →
          // click goes to billing where the user can actually unlock.
          if (item.locked) {
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => navigate("/app/billing")}
                data-testid={`${item.testid}-locked`}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md text-zinc-400 hover:bg-zinc-50 transition-colors"
                title="Creator & Pro"
              >
                <item.icon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1 text-left">{item.label}</span>
                <Lock className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => onNavigate && onNavigate()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {item.isNew && (
                <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500 text-white">
                  New
                </span>
              )}
              {item.badge > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-black rounded-full ${
                    item.pendingStyle === "pending"
                      ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white animate-pulse shadow-sm"
                      : item.pendingStyle === "done"
                        ? "bg-gradient-to-br from-emerald-400 to-green-600 text-white shadow-sm"
                        : "bg-red-600 text-white"
                  }`}
                  data-testid={`${item.testid}-badge`}
                  title={
                    item.pendingStyle === "pending" ? "AI reply in progress"
                    : item.pendingStyle === "done" ? "Reply ready"
                    : undefined
                  }
                >
                  {item.pendingStyle === "done" ? `✓ ${item.badge}` : (item.badge > 99 ? "99+" : item.badge)}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-zinc-200 space-y-2">
        {/* Bug report — admins triage from the Admin panel so we hide this for them. */}
        {!user?.is_admin && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-bug-report"))}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
            data-testid="sidebar-bug-report-btn"
          >
            <Bug className="w-4 h-4" />
            <span className="flex-1 text-left">{t("bug_open")}</span>
          </button>
        )}
        <div className="relative">
          <NavLink
            to="/app/billing"
            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm border ${planColorClass}`}
            data-testid="plan-badge"
          >
            <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-xs">
              <PlanIcon className="w-3.5 h-3.5" />
              {planLabel}
            </span>
            <span className="text-[10px] font-semibold opacity-80">{t("side_upgrade")} →</span>
          </NavLink>
          {/* TRIAL pill — shown only while the user is on the auto-granted
              free trial. Sits visually attached to the plan badge so users
              instantly understand their PRO access is temporary. */}
          {user?.is_trial && (
            <NavLink
              to="/app/pricing"
              data-testid="sidebar-trial-pill"
              title={t("trial_pill_tooltip")}
              className="mt-1.5 flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-white shadow-sm hover:shadow-md transition-shadow"
              style={{
                background:
                  (user.trial_days_remaining ?? 0) <= 2
                    ? "linear-gradient(135deg, #dc2626 0%, #f97316 100%)"
                    : "linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)",
              }}
            >
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                {t("trial_pill_label", { days: user.trial_days_remaining ?? 0 })}
              </span>
              <span className="opacity-90">{t("trial_pill_cta")} →</span>
            </NavLink>
          )}
        </div>
        <div className="px-3 py-2 text-xs text-zinc-500 truncate" data-testid="user-email">
          {user?.email}
        </div>
        {/* Theme switcher — swatches + palette-picker for a fully custom pastel */}
        {(() => {
          const visibleThemes = themes.filter((th) => !isHidden(th.flag));
          if (visibleThemes.length === 0) return null;
          return (
            <div className="px-3 py-2 mt-1 border-t border-zinc-200/60">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">{t("side_theme") || "Theme"}</div>
              <div className="flex items-center gap-1.5" data-testid="sidebar-theme-switcher">
                {visibleThemes.map((th) => (
                  <button
                    key={th.id}
                    onClick={() => setTheme(th.id)}
                    data-testid={`theme-${th.id}`}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${theme === th.id ? "border-zinc-900 scale-110 shadow-md" : "border-zinc-300 hover:scale-105"}`}
                    style={{ background: th.swatch }}
                    title={th.label}
                    aria-label={th.label}
                  />
                ))}
                {/* Palette / paintbrush swatch — opens the native colour
                    picker. Whatever the user chooses is auto-pastelised
                    in ThemeContext.js so the result is always a light,
                    readable tint. */}
                <label
                  data-testid="theme-custom-picker"
                  className={`relative w-6 h-6 rounded-full border-2 cursor-pointer transition-all flex items-center justify-center ${theme === "custom" ? "border-zinc-900 scale-110 shadow-md" : "border-zinc-300 hover:scale-105"}`}
                  style={{ background: theme === "custom" ? customColor : "conic-gradient(from 0deg, #fecaca, #fde68a, #bbf7d0, #bae6fd, #ddd6fe, #fecaca)" }}
                  title={t("side_theme_custom") || "Pick a custom light colour"}
                >
                  <Palette className="w-3 h-3 text-zinc-900/70 mix-blend-multiply pointer-events-none" />
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="theme-custom-picker-input"
                    aria-label="Custom theme colour"
                  />
                </label>
              </div>
            </div>
          );
        })()}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t("side_logout")}
        </button>
        {/* Apple 5.1.1(v) — visible in-app path to delete your account. */}
        <button
          onClick={handleDeleteAccount}
          data-testid="delete-account-btn"
          className="w-full flex items-center gap-2 px-3 py-2 mt-1 text-xs text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          {t("delete_account") || "Delete my account"}
        </button>
      </div>
      <DeleteAccountDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
      />
    </aside>
  );
}
